const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

const fmtDec = (n, d = 2) =>
  (Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const fmtBaht = (n) => `฿ ${fmtInt(n)}`;

const fmtCbm = (n) => `${fmtDec(n)} CBM`;

const fmtTrend = (change) => {
  if (!change || change.changePct == null) return null;
  const pct = Number(change.changePct);
  const sign = pct > 0 ? "+" : ""; // ค่าลบมีเครื่องหมาย '-' จาก toFixed อยู่แล้ว
  return { text: `${sign}${pct.toFixed(1)}%`, positive: change.direction !== "down" };
};

const KPI_META = [
  { key: "revenue", title: "Revenue", icon: "revenue", field: "revenue", fmt: fmtBaht },
  { key: "shipments", title: "Total Shipments", icon: "shipments", field: "totalShipments", fmt: fmtCbm },
  { key: "delivered", title: "Delivered Packages", icon: "delivered", field: "deliveredPackages", fmt: fmtCbm },
  {
    key: "destination",
    title: "Destination Transport",
    icon: "destination",
    field: "destinationTransport",
    fmt: fmtCbm,
  },
];

const mapCodes = (obj, warehouses) => {
  const out = {};
  (warehouses || []).forEach((code) => {
    out[code] = obj && obj[code] != null ? fmtDec(obj[code]) : "-";
  });
  return out;
};

const adaptSummary = (summary = {}) => ({
  title: summary.title,
  keyLabel: summary.keyLabel,
  warehouses: summary.warehouses || [],
  periods: (summary.periods || []).map((p) => ({
    key: p.key,
    label: p.label,
    total: `${fmtDec(p.total)} CBM`,
    columnTotals: p.columnTotals
      ? {
          sea: mapCodes(p.columnTotals.sea, summary.warehouses),
          land: mapCodes(p.columnTotals.land, summary.warehouses),
        }
      : null,
    rows: (p.rows || []).map((r) => ({
      key: r.key,
      name: r.name,
      sea: mapCodes(r.sea, summary.warehouses),
      land: mapCodes(r.land, summary.warehouses),
    })),
  })),
});

const adaptFreight = (f) =>
  f
    ? {
        title: f.title,
        baht: fmtInt(f.baht),
        cbm: fmtDec(f.cbm),
        kg: fmtDec(f.kg),
        trend: fmtTrend(f.change)?.text,
        regions: (f.regions || []).map((r) => ({
          key: r.key,
          region: r.region,
          baht: fmtInt(r.baht),
          cbm: fmtDec(r.cbm),
          kg: fmtDec(r.kg),
        })),
      }
    : null;

export const adaptSummaries = (res) => ({
  inboundSummary: adaptSummary(res.inboundSummary),
  packageTypeSummary: adaptSummary(res.packageTypeSummary),
});

export const adaptInboundDrilldown = (res) => ({
  inboundSummary: adaptSummary(res.inboundSummary),
});

export const adaptKpis = (res) => ({
  kpiCards: KPI_META.map((m) => {
    const trend = fmtTrend(res.kpis?.changes?.[m.field]);
    return {
      key: m.key,
      title: m.title,
      icon: m.icon,
      value: m.fmt(res.kpis?.[m.field]),
      trend: trend?.text,
      trendLabel: trend ? "vs Last Month" : undefined,
      positive: trend ? trend.positive : true,
    };
  }),
});

export const adaptMetrics = (res) => ({
  outstanding: {
    total: fmtBaht(res.outstandingBalance?.total),
    byCompany: fmtBaht(res.outstandingBalance?.byCompany),
    byCustomer: fmtBaht(res.outstandingBalance?.byCustomer),
    byCustomerByCompany: (res.outstandingBalance?.byCustomerByCompany || []).map((row) => ({
      code: row.code,
      amount: fmtBaht(row.amount),
    })),
  },
  freight: {
    sea: adaptFreight(res.freight?.sea),
    land: adaptFreight(res.freight?.land),
  },
});

export const adaptOperations = (res) => {
  const status = res.shipmentStatus || { total: 0, buckets: [] };
  return {
    shipmentStatus: (status.buckets || []).map((b) => ({
      label: b.label,
      count: `${fmtDec(b.count)} CBM`,
      pct: status.total > 0 ? Math.round((b.count / status.total) * 100) : 0,
      color: b.color,
    })),
    shipmentTotal: status.total,
    shipmentTrend: res.shipmentTrend || { months: [], series: [] },
    companyTop: (res.companyTop || []).map((c) => ({
      rank: c.rank,
      code: c.code,
      volume: fmtInt(c.volume),
    })),
  };
};

// รวมทุก slice — ใช้กับ endpoint /executive-summary (fallback) ให้ผลเหมือนเดิม
export const adaptExecutiveSummary = (res) => ({
  ...adaptSummaries(res),
  ...adaptKpis(res),
  ...adaptMetrics(res),
  ...adaptOperations(res),
});
